<template>
  <div class="LabPartnerDetail">
    <div class="LabPartnerDetail-back">
      <router-link to="/lab-partners">← Back to Lab Partners</router-link>
    </div>

    <div v-if="loading && !partner" class="LabPartners-loading">
      <p>Loading partner…</p>
    </div>

    <div v-if="error" class="LabPartners-error">
      <p>{{ error }}</p>
    </div>

    <div v-if="success" class="LabPartners-success">
      <p>{{ success }}</p>
    </div>

    <template v-if="partner">
      <h1 class="LabPartnerDetail-title">{{ partner.name }}</h1>
      <p class="LabPartnerDetail-meta">
        Code: <strong>{{ partner.code }}</strong>
        <span v-if="partner.email"> · {{ partner.email }}</span>
        <span> · Status: {{ partner.status }}</span>
        <span v-if="partner.user_id"> · User ID: {{ partner.user_id }}</span>
      </p>

      <div class="LabPartnerDetail-panel">
        <p>
          Accrued commission:
          <strong>£{{ earningsSummary.accrued_total || '0.00' }}</strong>
        </p>
        <p class="u-textMuted">
          Tickets referred: {{ earningsSummary.ticket_count || 0 }}
        </p>
      </div>

      <div class="LabPartnerDetail-panel">
        <h2>Payout preview</h2>
        <p class="u-textMuted">
          Date range uses accrual dates (when commission was earned), not event payment dates.
        </p>

        <div class="LabPartners-filters">
          <div class="LabPartners-filterGroup">
            <label for="payout-date-from">From</label>
            <input id="payout-date-from" type="date" v-model="dateFrom">
          </div>
          <div class="LabPartners-filterGroup">
            <label for="payout-date-to">To</label>
            <input id="payout-date-to" type="date" v-model="dateTo">
          </div>
          <button class="Button" :disabled="previewLoading" @click="loadPayoutPreview">
            {{ previewLoading ? 'Previewing…' : 'Preview payout' }}
          </button>
        </div>

        <div v-if="payoutPreview">
          <p>
            Payable total:
            <strong>£{{ payoutPreview.payable_total }}</strong>
            ({{ payoutPreview.payable_count }} earning<span v-if="payoutPreview.payable_count !== 1">s</span>)
          </p>
          <p>
            Minimum threshold: £{{ payoutPreview.minimum_threshold }}
            <span v-if="payoutPreview.meets_threshold" class="Chip Chip--success">Meets threshold</span>
            <span v-else class="Chip Chip--warning">Below threshold</span>
          </p>
          <p>
            Bank details on file:
            <strong>{{ payoutPreview.bank_details_on_file ? 'Yes' : 'No' }}</strong>
          </p>
          <p>
            Payout ready:
            <strong>{{ payoutPreview.payout_ready ? 'Yes' : 'No' }}</strong>
          </p>
          <p v-if="payoutPreview.excluded_count > 0" class="u-textMuted">
            {{ payoutPreview.excluded_count }} accrued earning(s) excluded (refunded tickets, cancelled events, etc.).
          </p>
        </div>
      </div>

      <div class="LabPartnerDetail-panel">
        <h2>Generate payout</h2>
        <p class="u-textMuted">
          Generate an affiliate payout invoice for manual bank transfer on Wednesday alongside organiser payments.
        </p>

        <div class="LabPartnerDetail-actions">
          <button
            class="Button"
            :disabled="!canGenerateInvoice || actionLoading"
            @click="generateInvoice">
            {{ actionLoading ? 'Working…' : 'Generate payout invoice' }}
          </button>
          <button
            class="Button Button--secondary"
            :disabled="!lastInvoice || actionLoading"
            @click="sendInvoice">
            Send statement
          </button>
          <button
            class="Button"
            :disabled="!canMarkPaid || actionLoading"
            @click="markPaid">
            Mark as paid
          </button>
        </div>

        <div v-if="lastInvoice" class="LabPartnerDetail-invoice">
          <p>
            Invoice <strong>{{ lastInvoice.invoice_reference }}</strong>
            · Status: <strong>{{ lastInvoice.status }}</strong>
            · Net: <strong>£{{ lastInvoice.total_net }}</strong>
          </p>
          <a
            v-if="lastInvoice.pdf_url"
            :href="lastInvoice.pdf_url"
            target="_blank"
            rel="noopener noreferrer">
            Download PDF
          </a>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import './LabPartners.scss';
import axios from 'axios';
import cookieModel from '../../models/cookie';
import errorModel from '../../models/error';
import { getDefaultInvoiceDateRange, getTodayLocalYMD } from '../../utils/date-local';
import {
  setAuthHeader,
  fetchLabPartner,
  fetchPayoutPreview,
  generatePayoutInvoice,
  markReferralPayoutPaid,
  sendReferralPayoutInvoice,
} from '../../models/lab-partners';

export default {
  name: 'LabPartnerDetail',
  mounted() {
    this.loadPartner();
  },
  watch: {
    '$route.params.id': function () {
      this.loadPartner();
    },
  },
  computed: {
    canGenerateInvoice() {
      return this.payoutPreview
        && this.payoutPreview.meets_threshold
        && this.payoutPreview.payout_ready
        && this.payoutPreview.bank_details_on_file;
    },
    canMarkPaid() {
      return this.lastInvoice
        && this.lastInvoice.invoice_id
        && this.lastInvoice.status !== 'paid';
    },
  },
  methods: {
    loadPartner: function () {
      const vm = this;
      const partnerId = vm.$route.params.id;

      if (!partnerId) {
        vm.error = 'Partner not found';
        return;
      }

      vm.loading = true;
      vm.error = '';
      vm.success = '';
      vm.payoutPreview = null;
      vm.lastInvoice = null;

      setAuthHeader(vm.axiosInstance, cookieModel);

      fetchLabPartner(vm.axiosInstance, vm.baseUrl, partnerId)
        .then(function (response) {
          const payload = response.data || {};
          vm.partner = payload.partner || null;
          vm.earningsSummary = payload.earnings_summary || {};
          vm.loading = false;
          vm.loadPayoutPreview();
        })
        .catch(function (error) {
          vm.error = errorModel.apiError(error, vm.$router);
          vm.loading = false;
        });
    },

    loadPayoutPreview: function () {
      const vm = this;
      const partnerId = vm.$route.params.id;

      if (!partnerId || !vm.dateFrom || !vm.dateTo) {
        return;
      }

      vm.previewLoading = true;
      setAuthHeader(vm.axiosInstance, cookieModel);

      fetchPayoutPreview(vm.axiosInstance, vm.baseUrl, partnerId, vm.dateFrom, vm.dateTo)
        .then(function (response) {
          vm.payoutPreview = (response.data && response.data.data) ? response.data.data : null;
          vm.previewLoading = false;
        })
        .catch(function (error) {
          vm.error = errorModel.apiError(error, vm.$router);
          vm.previewLoading = false;
        });
    },

    generateInvoice: function () {
      const vm = this;
      const partnerId = vm.$route.params.id;

      vm.actionLoading = true;
      vm.error = '';
      vm.success = '';
      setAuthHeader(vm.axiosInstance, cookieModel);

      generatePayoutInvoice(vm.axiosInstance, vm.baseUrl, partnerId, {
        date_from: vm.dateFrom,
        date_to: vm.dateTo,
        auto_send: true,
      })
        .then(function (response) {
          const payload = response.data || {};
          vm.lastInvoice = payload.data || null;
          vm.success = payload.message || 'Affiliate payout invoice generated';
          vm.actionLoading = false;
          vm.loadPartner();
        })
        .catch(function (error) {
          vm.error = errorModel.apiError(error, vm.$router);
          vm.actionLoading = false;
        });
    },

    sendInvoice: function () {
      const vm = this;

      if (!vm.lastInvoice || !vm.lastInvoice.invoice_id) {
        return;
      }

      vm.actionLoading = true;
      vm.error = '';
      setAuthHeader(vm.axiosInstance, cookieModel);

      sendReferralPayoutInvoice(vm.axiosInstance, vm.baseUrl, vm.lastInvoice.invoice_id)
        .then(function (response) {
          vm.success = (response.data && response.data.message) ? response.data.message : 'Payout statement sent';
          vm.actionLoading = false;
        })
        .catch(function (error) {
          vm.error = errorModel.apiError(error, vm.$router);
          vm.actionLoading = false;
        });
    },

    markPaid: function () {
      const vm = this;

      if (!vm.lastInvoice || !vm.lastInvoice.invoice_id) {
        return;
      }

      vm.actionLoading = true;
      vm.error = '';
      setAuthHeader(vm.axiosInstance, cookieModel);

      markReferralPayoutPaid(vm.axiosInstance, vm.baseUrl, vm.lastInvoice.invoice_id, {
        payment_date: getTodayLocalYMD(),
      })
        .then(function (response) {
          vm.success = (response.data && response.data.message) ? response.data.message : 'Affiliate payout marked as paid';
          if (response.data && response.data.data) {
            vm.lastInvoice = Object.assign({}, vm.lastInvoice, response.data.data, {
              status: response.data.data.status,
            });
          }
          vm.actionLoading = false;
          vm.loadPartner();
        })
        .catch(function (error) {
          vm.error = errorModel.apiError(error, vm.$router);
          vm.actionLoading = false;
        });
    },
  },
  data: function () {
    const defaultRange = getDefaultInvoiceDateRange();

    return {
      baseUrl: process.env.VUE_APP_API_URL,
      axiosInstance: axios.create({
        baseURL: process.env.VUE_APP_API_URL,
      }),
      loading: false,
      previewLoading: false,
      actionLoading: false,
      error: '',
      success: '',
      partner: null,
      earningsSummary: {},
      payoutPreview: null,
      lastInvoice: null,
      dateFrom: defaultRange.date_from,
      dateTo: defaultRange.date_to,
    };
  },
};
</script>
